// src/components/Layout.tsx

import { useState } from "react";
import { Outlet } from "react-router-dom";
import { useT } from "@/lib/translation";
import { portfolioDisplayName } from "@/features/portfolios/utils/portfolioUtils";
import { SlidersHorizontal } from "lucide-react";
import { Navigation } from "./Navigation";
import { ThemeToggle } from "./theme-toggle";
import { UserLanguageMenu } from "@/components/UserLanguageMenu";
import { PrivacyToggle } from "./PrivacyToggle";
import SocialLinks from "@/components/site/SocialLinks";
import { usePortfolio } from "@/contexts/PortfolioContext";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { BrokerageLogo } from "@/components/shared/BrokerageLogo";
import OnboardingModals from "./OnboardingModals";
import ErrorBoundary from "@/components/shared/ErrorBoundary";
import PageErrorFallback from "@/components/shared/PageErrorFallback";
import PageContentSuspense from "@/components/shared/PageContentSuspense";
import { NotificationBell } from "@/features/notifications/components/NotificationBell";
import BillingGate from "@/components/billing/BillingGate";
import { UpgradeButton } from "@/components/billing/UpgradeButton";

// TECH-DEBT: duplicated in ResearchLayout — mobile settings Sheet, header controls
// (PrivacyToggle/ThemeToggle/NotificationBell), ErrorBoundary/PageErrorFallback
// pattern, and footer text are identical. Extract a shared BaseLayout component.
const Layout = () => {
  const t = useT();
  const [settingsOpen, setSettingsOpen] = useState(false);
  const {
    portfolios,
    combinedPortfolio,
    selectedPortfolio,
    setSelectedPortfolioId,
    isLoading,
  } = usePortfolio();

  const handlePortfolioChange = (portfolioId: string) => {
    const id = Number(portfolioId);
    setSelectedPortfolioId(id);
  };

  const hasAnyPortfolio = portfolios.length > 0 || combinedPortfolio !== null;

  return (
    <div className="relative flex min-h-screen">
      <Navigation />
      <main className="flex-1 min-w-0 sm:ml-60 flex flex-col min-h-screen">
        <OnboardingModals portfolios={portfolios} isLoading={isLoading} />
        <header className="sticky top-0 z-30 flex items-center justify-between h-14 border-b bg-background px-14 sm:px-6">
          <div className="flex items-center gap-4">
            {isLoading ? (
              <Skeleton className="h-9 w-32 sm:w-48" />
            ) : (
              <Select
                value={selectedPortfolio?.id.toString() ?? ""}
                onValueChange={handlePortfolioChange}
                disabled={!hasAnyPortfolio}
              >
                <SelectTrigger className="w-32 sm:w-48">
                  <SelectValue placeholder={t("nav:layout.selectPortfolio")} />
                </SelectTrigger>
                <SelectContent>
                  {combinedPortfolio && (
                    <SelectItem
                      key={combinedPortfolio.id}
                      value={combinedPortfolio.id.toString()}
                    >
                      <div className="flex items-center gap-2">
                        <BrokerageLogo
                          brokerage={combinedPortfolio.brokerage}
                          size="sm"
                        />
                        <span className="truncate">
                          {portfolioDisplayName(combinedPortfolio, t)}
                        </span>
                      </div>
                    </SelectItem>
                  )}
                  {portfolios.map((portfolio) => (
                    <SelectItem
                      key={portfolio.id}
                      value={portfolio.id.toString()}
                    >
                      <div className="flex items-center gap-2">
                        <BrokerageLogo
                          brokerage={portfolio.brokerage}
                          customAssetClass={portfolio.custom_asset_class}
                          logoKey={portfolio.logo_key}
                          size="sm"
                        />
                        <span className="truncate">
                          {portfolioDisplayName(portfolio, t)}
                        </span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>

          <div className="flex items-center justify-end gap-4">
            <div className="hidden sm:flex items-center gap-4">
              <UpgradeButton />
              <UserLanguageMenu />
              <PrivacyToggle />
              <ThemeToggle />
              <NotificationBell />
            </div>
          </div>
        </header>
        {/* Mobile: fixed settings + bell buttons at right edge */}
        <div className="fixed top-3 right-14 z-40 sm:hidden">
          <NotificationBell />
        </div>
        <Button
          variant="ghost"
          size="icon"
          className="fixed top-3 right-4 z-40 h-8 w-8 sm:hidden"
          onClick={() => setSettingsOpen(true)}
        >
          <SlidersHorizontal className="h-4 w-4" />
        </Button>

        <Sheet open={settingsOpen} onOpenChange={setSettingsOpen}>
          <SheetContent side="right" className="w-64">
            <SheetHeader>
              <SheetTitle>{t("nav:layout.display")}</SheetTitle>
            </SheetHeader>
            <div className="flex flex-col gap-6 pt-6">
              <UpgradeButton className="w-full" />
              <UserLanguageMenu withLabel />
              <div>
                <p className="text-sm font-medium mb-3 text-muted-foreground">
                  {t("nav:layout.privacy")}
                </p>
                <PrivacyToggle />
              </div>
              <div>
                <p className="text-sm font-medium mb-3 text-muted-foreground">
                  {t("nav:layout.theme")}
                </p>
                <ThemeToggle />
              </div>
            </div>
          </SheetContent>
        </Sheet>
        <div className="p-6 bg-muted/50 flex-1 overflow-x-clip">
          <ErrorBoundary
            fallbackRender={({ resetError }) => (
              <PageErrorFallback onReset={resetError} />
            )}
          >
            <BillingGate>
              <PageContentSuspense>
                <Outlet />
              </PageContentSuspense>
            </BillingGate>
          </ErrorBoundary>
        </div>
        <footer className="sm:ml-0 px-6 py-3 border-t grid grid-cols-[1fr_auto_1fr] items-center gap-2">
          <div className="text-xs text-muted-foreground">
            <SocialLinks withLabel={false} />
          </div>
          <p className="text-xs text-muted-foreground text-center whitespace-nowrap">
            {t("nav:layout.disclaimer")}
          </p>
          <div />
        </footer>
      </main>
    </div>
  );
};

export default Layout;
